import { ArticlesZeroState } from "@/components/articles/ArticlesZeroState";
import { ProgressBadge } from "@/components/articles/status/ProgressBadge";
import { ProgressBar } from "@/components/articles/status/ProgressBar";
import { StatusActions } from "@/components/articles/status/StatusActions";
import { StatusFilter } from "@/components/articles/status/StatusFilter";
import ArticleProgressModal from "@/components/articles/ArticleProgressModal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useBrand } from "@/contexts/brand-context";
import {
  articleStatusService,
  ArticleWithProgress,
} from "@/services/articleStatus";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format } from "date-fns";
import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { pb } from "@/lib/pocketbase";

export default function Content() {
  const { currentBrand } = useBrand();
  const location = useLocation();
  const [articles, setArticles] = useState<ArticleWithProgress[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<
    ArticleWithProgress[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedProgressArticle, setSelectedProgressArticle] = useState<
    string | null
  >(null);
  // Note: Polling logic removed in favor of PocketBase real-time subscriptions

  // Fetch articles with status
  const fetchArticles = async () => {
    if (!currentBrand) return;

    try {
      setIsLoading(true);
      const articlesWithStatus =
        await articleStatusService.fetchArticlesWithStatus(currentBrand.id);
      setArticles(articlesWithStatus);
    } catch (error) {
      console.error("Error fetching articles:", error);
      toast.error("Failed to load articles");
    } finally {
      setIsLoading(false);
    }
  };

  // Filter articles when status filter changes
  useEffect(() => {
    const filtered = articleStatusService.filterArticles(
      articles,
      statusFilter,
    );
    setFilteredArticles(filtered);
  }, [articles, statusFilter]);

  // Initial fetch
  useEffect(() => {
    fetchArticles();
  }, [currentBrand]);

  // Handle updated post from navigation state
  useEffect(() => {
    if (location.state?.updatedPost) {
      const { id } = location.state.updatedPost;

      // Update the specific article in the local state
      setArticles((prevArticles) =>
        prevArticles.map((article) =>
          article.id === id ? { ...article, status: "published" } : article,
        ),
      );

      // Clear the navigation state to prevent re-applying on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Subscribe to status updates
  useEffect(() => {
    const unsubscribe = articleStatusService.subscribe((updatedArticles) => {
      setArticles(updatedArticles);
    });

    return unsubscribe;
  }, []);

  // Track subscribed post IDs to avoid duplicate subscriptions
  const subscribedPostsRef = useRef<Set<string>>(new Set());

  // Subscribe to real-time updates for generating posts
  // Only subscribe if the progress modal is NOT open for that specific post
  useEffect(() => {
    if (!currentBrand) return;

    const subscribeToGeneratingPosts = async () => {
      // Find all posts with generating/in_progress status
      const generatingPosts = articles.filter(
        (article) => article.status === "in_progress",
      );

      // Subscribe to each generating post if not already subscribed
      // AND not currently being viewed in the progress modal
      for (const post of generatingPosts) {
        // Skip if this post is currently selected in the progress modal
        if (selectedProgressArticle === post.id) {
          continue;
        }

        if (!subscribedPostsRef.current.has(post.id)) {
          try {
            subscribedPostsRef.current.add(post.id);

            await pb.collection("posts").subscribe(post.id, (e) => {
              console.log(
                "[LIST] Received update for post:",
                post.id,
                e.action,
                e.record,
              );

              if (e.action === "update") {
                const updatedRecord = e.record;

                // Check if status changed from generating to draft/published
                if (
                  updatedRecord.status === "draft" ||
                  updatedRecord.status === "published"
                ) {
                  // Update the local articles state
                  setArticles((prevArticles) =>
                    prevArticles.map((article) => {
                      if (article.id === post.id) {
                        return {
                          ...article,
                          status:
                            updatedRecord.status === "published"
                              ? "published"
                              : "draft",
                          progress: undefined, // Clear progress when done
                        };
                      }
                      return article;
                    }),
                  );

                  // Unsubscribe from this post since it's done
                  pb.collection("posts").unsubscribe(post.id);
                  subscribedPostsRef.current.delete(post.id);

                  // Show success notification
                  toast.success(
                    `Article "${post.title}" has been generated successfully!`,
                  );

                  // Refresh articles to get latest data
                  fetchArticles();
                } else if (updatedRecord.status === "failed") {
                  console.log("[LIST] Post generation failed:", post.id);

                  // Update the local articles state
                  setArticles((prevArticles) =>
                    prevArticles.map((article) => {
                      if (article.id === post.id) {
                        return {
                          ...article,
                          status: "failed",
                          progress: undefined,
                        };
                      }
                      return article;
                    }),
                  );

                  // Unsubscribe from this post
                  pb.collection("posts").unsubscribe(post.id);
                  subscribedPostsRef.current.delete(post.id);

                  // Show error notification
                  toast.error(`Article "${post.title}" generation failed.`);

                  // Refresh articles
                  fetchArticles();
                }
              }
            });
          } catch (error) {
            console.error("[LIST] Error subscribing to post:", post.id, error);
            subscribedPostsRef.current.delete(post.id);
          }
        }
      }
    };

    subscribeToGeneratingPosts();

    // Cleanup function
    return () => {
      // Unsubscribe from all posts
      subscribedPostsRef.current.forEach((postId) => {
        try {
          pb.collection("posts").unsubscribe(postId);
        } catch (error) {
          console.warn("[LIST] Error unsubscribing from post:", postId, error);
        }
      });
      subscribedPostsRef.current.clear();
    };
  }, [articles, currentBrand, fetchArticles, selectedProgressArticle]);

  // Auto-refresh for in-progress articles
  useEffect(() => {
    // Note: Progress tracking is now handled via PocketBase real-time subscriptions
    // in individual components using useArticleProgress hook

    // Auto-refresh the entire list every 30 seconds for in-progress articles
    const refreshInterval = setInterval(() => {
      if (articles.some((article) => article.status === "in_progress")) {
        fetchArticles();
      }
    }, 30000);

    return () => {
      clearInterval(refreshInterval);
    };
  }, [articles]);

  // Handle retry generation
  const handleRetry = async (article: ArticleWithProgress) => {
    if (!currentBrand) return;

    try {
      const postId = await articleStatusService.retryGeneration(
        currentBrand.id,
        article.topic || article.title,
        undefined, // keyword
      );

      if (postId) {
        toast.success("Article generation restarted");
        // Real-time progress will be handled by individual components
        await fetchArticles(); // Refresh the list
      } else {
        toast.error("Failed to restart generation");
      }
    } catch (error) {
      console.error("Error retrying generation:", error);
      toast.error("Failed to restart generation");
    }
  };

  // Handle generate now for scheduled articles
  const handleGenerateNow = async (article: ArticleWithProgress) => {
    if (!article.isScheduled) {
      toast.error("This action is only available for scheduled articles");
      return;
    }

    try {
      const postId = await articleStatusService.generateScheduledArticle(
        article.id,
      );

      if (postId) {
        toast.success("Article generation started");
        // Real-time progress will be handled by individual components
        await fetchArticles(); // Refresh the list
      } else {
        toast.error("Failed to start generation");
      }
    } catch (error) {
      console.error("Error generating scheduled article:", error);
      toast.error("Failed to start generation");
    }
  };

  // Handle showing progress for generating articles
  const handleShowProgress = (articleId: string) => {
    // Unsubscribe from List subscription if exists
    if (subscribedPostsRef.current.has(articleId)) {
      try {
        pb.collection("posts").unsubscribe(articleId);
        subscribedPostsRef.current.delete(articleId);
      } catch (error) {
        console.warn("[LIST] Error unsubscribing from post:", error);
      }
    }

    setSelectedProgressArticle(articleId);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup listeners
      articleStatusService.cleanup();

      // Cleanup any remaining post subscriptions
      subscribedPostsRef.current.forEach((postId) => {
        try {
          pb.collection("posts").unsubscribe(postId);
        } catch (error) {
          console.warn(
            "[LIST] Error unsubscribing from post on unmount:",
            postId,
            error,
          );
        }
      });
      subscribedPostsRef.current.clear();
    };
  }, []);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Articles</h2>
          <p className="text-muted-foreground">
            Manage and analyze your content portfolio.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            asChild
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <Link to="/articles/new">
              <FontAwesomeIcon icon={faPlus} className="mr-2 h-5 w-5" /> Create
              New
            </Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>Content Library</CardTitle>
          <StatusFilter
            value={statusFilter}
            onValueChange={setStatusFilter}
            className="w-[200px]"
          />
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <p>Loading articles...</p>
            </div>
          ) : filteredArticles.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredArticles.map((article) => (
                  <TableRow key={article.id}>
                    <TableCell className="font-medium">
                      <Link
                        to={`/articles/${article.id}`}
                        className="hover:text-blue-500 hover:underline underline-offset-4 cursor-pointer"
                      >
                        {article.title}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-2">
                        <ProgressBadge
                          status={article.status}
                          progress={article.progress?.percentage}
                        />
                        {article.status === "in_progress" &&
                          article.progress && (
                            <div className="min-w-[140px]">
                              <ProgressBar
                                progress={article.progress.percentage}
                                stage={article.progress.stage}
                                variant="simple"
                                size="sm"
                                showPercentage={false}
                              />
                            </div>
                          )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {format(
                        new Date(article.scheduleDate || article.created),
                        "MMM dd, yyyy",
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusActions
                        articleId={article.id}
                        status={article.status}
                        onRetry={() => handleRetry(article)}
                        onGenerateNow={() => handleGenerateNow(article)}
                        onShowProgress={() => handleShowProgress(article.id)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : statusFilter === "all" ? (
            <ArticlesZeroState />
          ) : (
            <div className="flex items-center justify-center h-64 text-center">
              <div>
                <p className="text-lg font-medium text-muted-foreground">
                  No articles found
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  No articles match the current filter. Try changing the status
                  filter.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Article Progress Modal */}
      <ArticleProgressModal
        postId={selectedProgressArticle}
        onComplete={() => {
          // Refresh articles when generation completes
          fetchArticles();
        }}
        onClose={() => {
          setSelectedProgressArticle(null);
        }}
      />
    </div>
  );
}
